/**
 * Require size selection before Add to Cart — v3 "authoritative variant sync"
 * ---------------------------------------------------------------------------
 * Empress Athleticus custom. Prevents "first variant" (XS) adds on the product
 * page, the quick-buy modal, featured-product sections and the sticky bar.
 *
 * WHY v3 (2026-07-22 drop-day incident): customers picking M were still getting
 * XS in the cart. Confirmed mechanisms:
 *  1. Prestige only writes the chosen variant into the form's hidden
 *     <input name="id"> AFTER an awaited network fetch (theme.js
 *     renderForCombination). The gate used to re-arm the button the instant a
 *     radio was checked — in that fetch window an Add posts the server-default
 *     XS, and a failed/throttled fetch is cached forever (poisoned promise), so
 *     "M" NEVER lands for the rest of the session.
 *  2. The self-heal MutationObserver deferred via requestAnimationFrame, which
 *     is starved in Facebook/Instagram in-app browsers — the quick-buy modal
 *     was never gated there: XS pre-checked, armed, one tap = XS.
 *  3. Reopening quick-buy re-served XS-checked HTML while `interacted` stood
 *     the gate down.
 *
 * v3 design:
 *  - AUTHORITATIVE SYNC: each size radio carries data-sg-variant (stamped
 *    server-side in snippets/option-value.liquid). On selection AND again in
 *    the capture-phase submit guard (which runs before the theme's submit
 *    handler builds FormData), we write that variant id into the form's
 *    <input name="id"> ourselves. The form can no longer post a stale XS,
 *    regardless of the theme's fetch timing or cache state.
 *  - NO rAF: observer work defers via setTimeout (fires even where rAF is
 *    starved), PLUS a capture-phase pointerdown/focusin net that initialises
 *    any un-gated picker synchronously BEFORE the tap's click lands.
 *  - Quick-buy reopen: clicking any quick-buy opener resets that flow's
 *    "interacted" memory so the fresh XS-checked modal is re-deselected.
 *  - Graceful degradation: if data-sg-variant is absent (e.g. a future theme
 *    update replaces option-value.liquid) or the product has multiple option
 *    groups, behaviour falls back to v2 (radio-state gating only).
 *
 * Remove this file + its <script> tag + the inline guard in theme.liquid and
 * the data-sg-* attributes in option-value.liquid to revert.
 */
(function () {
  var GATE_LABEL = 'Select your size';
  var SOLD_OUT_LABEL = 'Sold out';
  var SELECTOR = 'variant-picker[context="main_product"], variant-picker[context="quick_buy"], variant-picker[context="featured_product"]';
  var interacted = Object.create(null); // form-id -> true once a size is picked

  function pickersIn(root) {
    // Gift cards pick an amount, not a size (variant-picker.liquid stamps data-sg-skip)
    return Array.prototype.slice.call((root || document).querySelectorAll(SELECTOR)).filter(function (p) { return !p.hasAttribute('data-sg-skip'); });
  }

  function groupsOf(picker) {
    var radios = picker.querySelectorAll('input[type="radio"][name]');
    var groups = Object.create(null);
    Array.prototype.forEach.call(radios, function (r) {
      (groups[r.name] = groups[r.name] || []).push(r);
    });
    return groups;
  }

  // Every option group must have a checked radio (single-value groups count once checked).
  function allOptionsChosen(picker) {
    var groups = groupsOf(picker);
    var names = Object.keys(groups);
    if (!names.length) return true;
    return names.every(function (name) {
      return groups[name].some(function (r) { return r.checked; });
    });
  }

  // The radio's `value` is the option-value ID, not the size text. The visible
  // size lives in the <label for="{radio.id}"> (radios are sr-only). Read that.
  function optionLabel(radio) {
    var id = radio.id;
    if (id) {
      var esc = (window.CSS && CSS.escape) ? CSS.escape(id) : id;
      var lbl = document.querySelector('label[for="' + esc + '"]');
      if (lbl) {
        var t = (lbl.textContent || '').replace(/\s+/g, ' ').trim();
        if (t) return t;
      }
    }
    return (radio.getAttribute('aria-label') || '').trim();
  }

  // The chosen option value(s), e.g. "M" or "Black / M" — for the button label.
  function selectedText(picker) {
    var groups = groupsOf(picker), out = [];
    Object.keys(groups).forEach(function (name) {
      var arr = groups[name];
      for (var i = 0; i < arr.length; i++) {
        if (arr[i].checked) { var v = optionLabel(arr[i]); if (v) out.push(v); break; }
      }
    });
    return out.join(' / ');
  }

  // True if at least one selectable (not disabled) option exists to choose from.
  function hasSelectableOption(picker) {
    var radios = picker.querySelectorAll('input[type="radio"][name]');
    return Array.prototype.some.call(radios, function (r) { return !r.disabled; });
  }

  function formIdOf(picker) { return picker.getAttribute('form-id') || ''; }

  function esc(s) { return (window.CSS && CSS.escape) ? CSS.escape(s) : s; }

  // All buy-buttons tied to this form: the main one AND the sticky bar's copy
  // (both render <buy-buttons form="{same id}">, so we must gate every match).
  function allBuyButtonsFor(picker) {
    var id = formIdOf(picker);
    if (!id) return [];
    return Array.prototype.slice.call(document.querySelectorAll('buy-buttons[form="' + esc(id) + '"]'));
  }

  function isStickyButtons(bb) {
    return !!(bb.closest && bb.closest('product-sticky-bar'));
  }

  function setLabel(btn, text) {
    if (btn && (btn.textContent || '').trim() !== text) btn.textContent = text;
  }

  /**
   * The variant the shopper's selection resolves to, read from the
   * data-sg-variant attribute stamped on the radio server-side.
   * Only authoritative for single-option-group products (this store's apparel:
   * one "Size" group). Multi-group products return null -> v2 fallback,
   * because a radio's stamped variant is relative to the other selections at
   * render time.
   */
  function resolveVariant(picker) {
    var groups = groupsOf(picker);
    var names = Object.keys(groups);
    if (names.length !== 1) return null;
    var checked = null;
    groups[names[0]].some(function (r) { if (r.checked) { checked = r; return true; } return false; });
    if (!checked) return null;
    var vid = checked.getAttribute('data-sg-variant');
    if (!vid) return null;
    return { id: vid, available: checked.getAttribute('data-sg-available') !== '0' };
  }

  /**
   * Write the resolved variant id into every <input name="id"> belonging to
   * the picker's form — the form can then never post a stale default, no
   * matter what state the theme's async rerender / render cache is in.
   * Returns the resolved variant (or null when not resolvable).
   */
  function syncVariantId(picker) {
    var resolved = resolveVariant(picker);
    if (!resolved) return null;
    var formId = formIdOf(picker);
    if (!formId) return resolved;
    var form = document.getElementById(formId);
    var inputs = [];
    if (form) inputs = inputs.concat(Array.prototype.slice.call(form.querySelectorAll('input[name="id"]')));
    inputs = inputs.concat(Array.prototype.slice.call(document.querySelectorAll('input[name="id"][form="' + esc(formId) + '"]')));
    inputs.forEach(function (input) {
      if (input.value !== resolved.id) input.value = resolved.id;
      if (input.disabled) input.disabled = false;
    });
    return resolved;
  }

  function refresh(picker) {
    // Don't gate a fully sold-out product (no selectable option) — leave the theme's own state.
    if (!hasSelectableOption(picker)) return;

    var chosen = allOptionsChosen(picker);
    // Authoritative sync the moment a full selection exists (and on every
    // refresh after) — closes the armed-button-posts-XS fetch window.
    var resolved = chosen ? syncVariantId(picker) : null;

    // Until a size is picked, the stock line ("In stock", "Only 6 units left") describes the server's
    // default XS, not the shopper's size. Hide it (space kept, so nothing jumps) until she picks.
    var info = picker.closest('.product-info') || picker.closest('.shopify-section');
    if (info) info.classList.toggle('size-gate-unchosen', !chosen);

    allBuyButtonsFor(picker).forEach(function (bb) {
      var atc = bb.querySelector('button[type="submit"]');
      if (atc) {
        if (!chosen) {
          // Remember the real label the first time we gate a given button element.
          if (atc.getAttribute('data-size-gated') !== '1' && !atc.disabled) {
            atc.setAttribute('data-atc-label', (atc.textContent || '').trim());
          }
          // Main button gets disabled. The sticky button stays clickable so its
          // click can scroll the shopper up to the size picker (guard below).
          if (!isStickyButtons(bb)) atc.disabled = true;
          setLabel(atc, GATE_LABEL);
          atc.setAttribute('data-size-gated', '1');
          atc.classList.add('size-gate-btn');
        } else if (resolved && !resolved.available) {
          // Chosen size is sold out (stamped server-side): keep it un-addable
          // without waiting for the theme's own rerender to say so.
          if (atc.getAttribute('data-size-gated') !== '1' && !atc.disabled) {
            atc.setAttribute('data-atc-label', (atc.textContent || '').trim());
          }
          atc.disabled = true;
          setLabel(atc, SOLD_OUT_LABEL);
          atc.setAttribute('data-size-gated', '1');
          atc.classList.remove('size-gate-btn');
        } else if (atc.getAttribute('data-size-gated') === '1') {
          // We gated it; hand control back to the theme's native button state.
          atc.disabled = false;
          var original = atc.getAttribute('data-atc-label');
          if (original) setLabel(atc, original);
          atc.removeAttribute('data-size-gated');
          atc.classList.remove('size-gate-btn');
        }
      }

      // Once a size is chosen, show it on the Add button (" · M") — main, sticky
      // and quick-buy. data-size-base holds the theme's pristine label so we
      // never stack it; skipped when disabled (sold out).
      if (chosen && atc && !atc.disabled) {
        var size = selectedText(picker);
        if (size) {
          if (!atc.getAttribute('data-size-base')) {
            atc.setAttribute('data-size-base', (atc.textContent || '').trim());
          }
          var desired = atc.getAttribute('data-size-base') + ' · ' + size;
          if ((atc.textContent || '').trim() !== desired) atc.textContent = desired;
        }
      }

      bb.classList.toggle('size-gate-hide-payment', !chosen);
    });
  }

  function deselect(picker) {
    var groups = groupsOf(picker);
    var changed = false;
    Object.keys(groups).forEach(function (name) {
      if (groups[name].length > 1) {
        groups[name].forEach(function (r) { if (r.checked) { r.checked = false; changed = true; } });
      }
    });
    // Repaint the picker. Chrome can keep the default size painted as picked after this
    // programmatic uncheck (Oct 2026: quick-buy XS stayed solid black while unticked). A
    // display toggle forces a fresh style pass; no frame is painted in between.
    if (changed) {
      var display = picker.style.display;
      picker.style.display = 'none';
      void picker.offsetHeight;
      picker.style.display = display;
    }
  }

  // A link that names her size on purpose lands with that size picked: the wishlist
  // emails send ?variant=ID&ee_size=ID (Oct 2026). Only when both match, only on the main
  // product page, and only when that size is the one the server checked and in stock.
  // Every other visit is gated exactly as before.
  var DEEP_SIZE = (function () {
    try {
      var q = new URLSearchParams(window.location.search);
      var v = q.get('ee_size');
      return v && /^\d+$/.test(v) && v === q.get('variant') ? v : null;
    } catch (e) { return null; }
  })();

  function deepLinked(picker) {
    if (!DEEP_SIZE || picker.getAttribute('context') !== 'main_product') return false;
    var r = picker.querySelector('input[type="radio"][data-sg-variant="' + DEEP_SIZE + '"]');
    return !!(r && r.checked && !r.disabled && r.getAttribute('data-sg-available') !== '0');
  }

  function init(picker) {
    if (!picker.__sizeGate) {
      picker.__sizeGate = true;
      if (deepLinked(picker)) interacted[formIdOf(picker)] = true;
      else if (!interacted[formIdOf(picker)] && hasSelectableOption(picker)) deselect(picker);
    }
    refresh(picker);
  }

  function handleAll() { pickersIn(document).forEach(init); }

  // Any picker the deferred pipeline hasn't reached yet gets initialised
  // synchronously — used by the interaction net below so the FIRST tap in a
  // freshly injected quick-buy modal lands on an already-gated button even in
  // environments where every deferred callback is starved.
  function initPending() {
    pickersIn(document).forEach(function (p) { if (!p.__sizeGate) init(p); });
  }

  // Shopper picked a size (delegated so it survives the theme replacing the picker).
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('input[type="radio"]')) return;
    var picker = t.closest ? t.closest(SELECTOR) : null;
    if (!picker || picker.hasAttribute('data-sg-skip')) return;
    interacted[formIdOf(picker)] = true;
    refresh(picker); // synchronous: variant id is written THIS tick, before any tap can submit
    setTimeout(function () { refresh(picker); }, 0); // and again after the theme reacts
  }, true);

  // Interaction net: before any tap or focus can operate a button, make sure
  // every picker on the page has been gated. Synchronous and idempotent; this
  // does not depend on MutationObserver timing, rAF or setTimeout having run.
  document.addEventListener('pointerdown', initPending, true);
  document.addEventListener('touchstart', initPending, true);
  document.addEventListener('focusin', initPending, true);

  // Quick-buy reopen: the modal is re-served with the default size pre-checked
  // and `interacted` from the previous open would stand the deselect down.
  // Any click on a quick-buy opener resets the memory for quick-buy flows.
  document.addEventListener('click', function (e) {
    var opener = (e.target && e.target.closest)
      ? e.target.closest('.product-card__quick-add-button, [aria-controls*="quick-buy" i]')
      : null;
    if (!opener) return;
    Object.keys(interacted).forEach(function (key) {
      if (key.indexOf('quick-buy') !== -1) delete interacted[key];
    });
  }, true);

  // Fail-safe: never allow an add-to-cart submit without a full selection —
  // and when there IS one, re-assert the authoritative variant id in capture
  // phase, before the theme's submit handler (bubble phase) serialises the
  // form. Even a poisoned theme render cache can no longer post a stale XS.
  // form.getAttribute('id'), NEVER form.id: product forms contain
  // <input name="id">, and a named control shadows the form's .id property.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    var formId = (form && form.getAttribute) ? form.getAttribute('id') : '';
    if (!formId) return;
    var picker = null;
    pickersIn(document).some(function (p) {
      if (formIdOf(p) === formId) { picker = p; return true; }
      return false;
    });
    if (!picker || !hasSelectableOption(picker)) return;
    if (!allOptionsChosen(picker)) {
      e.preventDefault();
      e.stopPropagation();
      refresh(picker);
      return;
    }
    var resolved = syncVariantId(picker);
    if (resolved && !resolved.available) {
      // Selection resolves to a sold-out variant: block rather than add.
      e.preventDefault();
      e.stopPropagation();
      refresh(picker);
    }
  }, true);

  // Click guard for buttons that don't go through a native form submit:
  //  - the sticky bar's Add (form-associated button, always clickable so it
  //    can scroll the shopper up to the picker when nothing is chosen)
  //  - the dynamic checkout / payment button (only CSS-hidden while gated;
  //    belt-and-braces block here)
  // When a full selection exists, re-assert the variant id before letting the
  // click through.
  document.addEventListener('click', function (e) {
    var el = (e.target && e.target.closest)
      ? e.target.closest('buy-buttons button[type="submit"], product-sticky-bar button[type="submit"], buy-buttons .shopify-payment-button')
      : null;
    if (!el) return;
    var bb = el.closest('buy-buttons') || el.closest('product-sticky-bar');
    var formId = bb && bb.getAttribute('form');
    if (!formId) return;
    var picker = null;
    pickersIn(document).some(function (p) { if (formIdOf(p) === formId) { picker = p; return true; } return false; });
    if (!picker || !hasSelectableOption(picker)) return;
    if (!allOptionsChosen(picker)) {
      e.preventDefault();
      e.stopPropagation();
      if (isStickyButtons(bb)) {
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var firstRadio = picker.querySelector('input[type="radio"]:not([disabled])');
        if (firstRadio) { try { firstRadio.focus({ preventScroll: true }); } catch (_) {} }
      }
      refresh(picker);
      return;
    }
    syncVariantId(picker);
  }, true);

  // Gated-button styling + hide dynamic checkout while gated + hide the Smile
  // points block inside the quick-buy modal only (it shows a raw [points_amount]
  // placeholder there because Smile's JS doesn't re-scan the injected modal;
  // it still renders correctly on the full product page).
  var style = document.createElement('style');
  style.textContent =
    '#quick-buy-modal-content [class*="smile" i],' +
    '#quick-buy-modal-content [id*="smile" i],' +
    '#quick-buy-modal-content .shopify-block:has([class*="smile" i]),' +
    '#quick-buy-modal-content .shopify-block:has([id*="smile" i]){display:none !important}' +
    // Match the PDP's selected-size styling (solid plum, theme.css --ee-plum) inside quick-buy.
    '#quick-buy-modal-content input[type="radio"]:checked + .block-swatch:not(.is-disabled){' +
      'background-color:#6B3D4E !important;color:#fff !important;border-color:#6B3D4E !important}' +
    '#quick-buy-modal-content .block-swatch:not(.is-disabled):hover,' +
    '#quick-buy-modal-content .block-swatch:not(.is-disabled):focus{' +
      'background-color:#6B3D4E !important;color:#fff !important;border-color:#6B3D4E !important}' +
    '.size-gate-hide-payment .shopify-payment-button{display:none !important;pointer-events:none !important}' +
    '.size-gate-unchosen variant-inventory{visibility:hidden}' +
    // Main + quick-buy gated button: transparent pink outline (original, unchanged).
    // The STICKY bar's gated button is styled separately from Theme settings
    // (see theme.liquid — product-sticky-bar .size-gate-btn).
    '.size-gate-btn{' +
      '--button-background:transparent;--button-text-color:#C4365A;' +
      'background:transparent !important;color:#C4365A !important;' +
      'border:2px solid #C4365A !important;box-shadow:none !important;' +
      'opacity:1 !important;cursor:not-allowed !important;' +
    '}';
  (document.head || document.documentElement).appendChild(style);

  function boot() {
    // Tell the inline pre-gate guard in theme.liquid that the full gate is
    // active - it stands down and this script owns gating from here. The
    // class also releases the pre-boot CSS cover (unpainted default size,
    // outlined buy button).
    window.__sizeGateReady = true;
    document.documentElement.classList.add('size-gate-ready');
    handleAll();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Self-heal: the theme replaces <buy-buttons>/<variant-picker> on selection and
  // in the quick-buy modal. Re-apply whenever relevant nodes are added.
  // Deferred via setTimeout, NEVER requestAnimationFrame: rAF is starved in
  // Facebook/Instagram in-app webviews and occluded tabs, which left injected
  // quick-buy modals permanently ungated (XS armed, one tap = XS).
  if (window.MutationObserver) {
    var scheduled = false;
    new MutationObserver(function (mutations) {
      if (scheduled) return;
      var relevant = false;
      for (var i = 0; i < mutations.length && !relevant; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && (n.matches(SELECTOR) || n.matches('buy-buttons'))) ||
              (n.querySelector && (n.querySelector(SELECTOR) || n.querySelector('buy-buttons')))) {
            relevant = true;
            break;
          }
        }
      }
      if (!relevant) return;
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        handleAll();
      }, 16);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
